import { Alert, AlertDescription } from '@ophelos/ui/components/alert';
import { Button } from '@ophelos/ui/components/button';
import { Card, CardContent } from '@ophelos/ui/components/card';
import { Separator } from '@ophelos/ui/components/separator';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { BottomActionBar } from '@/components/layout/BottomActionBar';
import { useTranslation } from '@/hooks/useTranslation';

interface DisclosureStepProps {
  onContinue: () => void;
  onBack: () => void;
  isLoading?: boolean;
  /** How many questions this assessment's tier asks. */
  questionCount: number;
  /** How many steps they arrive in — equal to the question count when asked one at a time. */
  sectionCount: number;
  /**
   * True when the customer already has a completed assessment and is being asked for a deeper
   * one. Without saying so, meeting the disclosure gate again straight after finishing reads
   * as the flow having lost their answers.
   */
  deeper?: boolean;
}

const STEP_KEYS = ['questions', 'estimate', 'support'] as const;

/**
 * The hard gate. Nothing is asked until the customer acknowledges here, and the
 * server enforces the same rule — answers 404 until `acknowledge-disclosure` lands.
 */
export function DisclosureStep({
  onContinue,
  onBack,
  isLoading = false,
  questionCount,
  sectionCount,
  deeper = false,
}: DisclosureStepProps) {
  const { t } = useTranslation();
  // Nineteen questions is a number that talks someone out of starting; six short sections
  // is the same work described by the shape they will actually meet. Where the tier asks
  // one at a time the two counts are equal, and questions is the truthful framing.
  const grouped = sectionCount !== questionCount;

  return (
    <div className="w-full stack items-center px-4">
      <div className="flex-1 w-full max-w-sm mx-auto space-y-6">
        <div className="pt-6">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="size-4" />
            {t('budgetAssessment.disclosure.back')}
          </button>
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground">{t('budgetAssessment.disclosure.title')}</h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {t(deeper ? 'budgetAssessment.disclosure.introDeeper' : 'budgetAssessment.disclosure.intro')}
          </p>
        </div>

        <Card>
          {STEP_KEYS.map((key, idx) => (
            <CardContent key={key} className="py-0">
              <div className="flex gap-4 items-start py-4">
                <div className="size-7 shrink-0 rounded-full bg-foreground text-background flex items-center justify-center text-xs font-semibold">
                  {idx + 1}
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground">{t(`budgetAssessment.disclosure.steps.${key}Title`)}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t(
                      `budgetAssessment.disclosure.steps.${key}Body${key === 'questions' && grouped ? 'Grouped' : ''}`,
                      { count: grouped ? sectionCount : questionCount },
                    )}
                  </p>
                </div>
              </div>
              {idx < STEP_KEYS.length - 1 && <Separator />}
            </CardContent>
          ))}
        </Card>

        <Alert variant="subtle">
          <AlertDescription>{t('budgetAssessment.disclosure.privacy')}</AlertDescription>
        </Alert>
      </div>

      <BottomActionBar>
        <Button size="xl" className="group w-full max-w-sm" onClick={onContinue} disabled={isLoading}>
          {t('budgetAssessment.disclosure.acknowledge')}
          <ChevronRight className="size-5 ml-2 group-hover:translate-x-1 transition-transform" />
        </Button>
      </BottomActionBar>
    </div>
  );
}
